import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

export const RECEIVABLES_API = "/api/v1/receivables";

// ---------------------------------------------------------------- formatting

export const money = (value) =>
  value === null || value === undefined || value === ""
    ? "-"
    : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : String(iso);
};

export const fmtDateTime = (instant) =>
  instant
    ? new Date(instant).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

export const fmtTime = (instant) =>
  instant ? new Date(instant).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "-";

/** Today's date as yyyy-MM-dd in the browser's local time. */
export const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Escapes text for the HTML written into print windows. */
export const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

export const sumBy = (rows, pick) => rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);

/** Joins an id list for a Spring List<Long> request parameter; empty means "all". */
export const idList = (ids) => (ids && ids.length ? ids.join(",") : undefined);

// ---------------------------------------------------------------- collection types

export const COLLECTION_TYPES = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque/DD" },
  { value: "EFUND_TRANSFER", label: "E-Fund Transfer" },
  { value: "CREDIT_NOTE", label: "Credit Note/Stock Return" },
  { value: "DEBIT_NOTE", label: "Debit Note" },
  { value: "DISCOUNT", label: "Discount" },
  { value: "EXPIRED", label: "Expired" },
  { value: "FC_PAYOUT", label: "FC Payout" },
  { value: "NO_PAYMENT", label: "No Payment" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "SHOP_CLOSE", label: "Shop Close" },
];

export const MARKER_TYPES = new Set(["NO_PAYMENT", "DELIVERED", "SHOP_CLOSE"]);

/**
 * Which instrument fields a collection type needs, mirroring the backend validation. A credit note takes a reference number
 * only on the Payments page, and only when the receivable settings turn it on (creditNoteReference).
 */
export const fieldsFor = (type, creditNoteReference = false) => ({
  amount: !MARKER_TYPES.has(type),
  reference: ["CHEQUE", "UPI", "EFUND_TRANSFER"].includes(type) || (type === "CREDIT_NOTE" && creditNoteReference),
  referenceLabel:
    type === "CHEQUE" ? "Cheque No" : type === "UPI" ? "UTR No" : type === "EFUND_TRANSFER" ? "Transaction No" : "Reference No",
  instrumentDate: ["CHEQUE", "UPI", "EFUND_TRANSFER"].includes(type),
  instrumentDateLabel: type === "CHEQUE" ? "Cheque Date" : "Transaction Date",
  bank: ["CHEQUE", "EFUND_TRANSFER"].includes(type),
  branch: type === "CHEQUE",
});

export const VISIT_DAYS = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WED", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
];

/**
 * Row colours for InvoiceFlags.highlight — a collection on the bill waiting for approval: red for a bounced cheque, orange for a
 * post-dated (or unapproved) cheque, green for UPI, yellow for e-fund transfer and grey for cash.
 */
export const HIGHLIGHT_STYLES = {
  BOUNCED: { background: "#fca5a5" },
  CHEQUE: { background: "#fdba74" },
  UPI: { background: "#86efac" },
  EFUND: { background: "#fde047" },
  CASH: { background: "#d1d5db" },
};

export const HIGHLIGHT_LEGEND = [
  { key: "BOUNCED", label: "Cheque bounced" },
  { key: "CHEQUE", label: "Post-dated / unapproved cheque" },
  { key: "UPI", label: "Unapproved UPI" },
  { key: "EFUND", label: "Unapproved e-fund transfer" },
  { key: "CASH", label: "Unapproved cash" },
];

// ---------------------------------------------------------------- UI bits

export const LoadingOverlay = ({ show, label = "Loading..." }) =>
  show ? (
    <div className="loading-overlay">
      <div className="spinner-card">
        <div className="spinner-ring" />
        <div className="spinner-label">{label}</div>
      </div>
    </div>
  ) : null;

export const EmptyRow = ({ colSpan, title = "No records found", subtitle }) => (
  <tr>
    <td colSpan={colSpan}>
      <div className="empty-state">
        <span className="empty-state-title">{title}</span>
        {subtitle ? <span className="empty-state-subtitle">{subtitle}</span> : null}
      </div>
    </td>
  </tr>
);

export const HighlightLegend = () => (
  <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", fontSize: "12px", margin: "10px 0 0" }}>
    {HIGHLIGHT_LEGEND.map((item) => (
      <span key={item.key} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <span style={{ width: 14, height: 14, borderRadius: 4, display: "inline-block", ...HIGHLIGHT_STYLES[item.key] }} />
        {item.label}
      </span>
    ))}
  </div>
);

export const Modal = ({ title, onClose, children, maxWidth = "760px" }) => (
  <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="modal-box" style={{ maxWidth }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">{title}</h3>
        <button className="close-btn" onClick={onClose}>
          Close
        </button>
      </div>
      {children}
    </div>
  </div>
);

/**
 * Asks for the OTP sent to the approver. `pending` is the ActionResult from the request step
 * ({ approvalRequestId, message, totalAmount }); `verifyPath(id)` builds the verify endpoint.
 */
export const OtpModal = ({ pending, verifyPath, onClose, onVerified }) => {
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);

  if (!pending) return null;

  const verify = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error("Enter the OTP");
      return;
    }
    setVerifying(true);
    try {
      const result = await api.post(verifyPath(pending.approvalRequestId), { otp: otp.trim() });
      toast.success(result?.message || "Approved");
      setOtp("");
      onVerified(result);
    } catch (error) {
      toast.error(error.message || "OTP verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal title="Enter OTP" onClose={onClose} maxWidth="420px">
      <form onSubmit={verify}>
        <div className="page-subtitle" style={{ marginBottom: "12px" }}>
          {pending.message}
          {pending.totalAmount !== null && pending.totalAmount !== undefined ? ` — total ₹${money(pending.totalAmount)}` : ""}
        </div>
        <div className="form-group">
          <label>OTP</label>
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            maxLength={10}
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={verifying}>
            {verifying ? "Verifying..." : "Verify & Approve"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/** Compact multi-select: a button that opens a checkbox list. Empty selection means "All". */
export const MultiSelect = ({ options, selected, onChange, placeholder = "All", width = 220 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const labelText =
    selected.length === 0
      ? placeholder
      : options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(", ");

  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <button
        type="button"
        className="secondary"
        onClick={() => setOpen((v) => !v)}
        style={{ width: "100%", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={labelText}
      >
        {labelText}
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            top: "calc(100% + 4px)",
            left: 0,
            width: Math.max(width, 240),
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-md)",
          }}
        >
          <label style={{ display: "flex", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--color-border)" }}>
            <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} />
            <strong>{placeholder}</strong>
          </label>
          {options.map((option) => (
            <label key={option.value} style={{ display: "flex", gap: 8, padding: "6px 10px", cursor: "pointer" }}>
              <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
              <span>{option.label}</span>
            </label>
          ))}
          {options.length === 0 ? <div style={{ padding: 10, color: "var(--color-text-muted)" }}>Nothing to choose</div> : null}
        </div>
      ) : null}
    </div>
  );
};

/** Type-ahead customer search against the receivable customer lookup. */
const pickerLabel = (customer) => (customer ? `${customer.retailerCode ? `${customer.retailerCode} - ` : ""}${customer.name || ""}` : "");

/**
 * Customer search by code, name or phone. `beatId` / `beatIds` / `salesmanUserId` narrow it to those beats / the salesman's
 * customers; with a narrowed scope the whole list shows on focus.
 */
export const CustomerPicker = ({ value, onChange, beatId, beatIds, salesmanUserId, placeholder = "Search customer code / name...", width = 280 }) => {
  const [query, setQuery] = useState(pickerLabel(value));
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const clearedByTyping = useRef(false);

  useEffect(() => {
    // keep the text when the selection was dropped because the user started typing; follow external resets otherwise
    if (value) setQuery(pickerLabel(value));
    else if (!clearedByTyping.current) setQuery("");
    clearedByTyping.current = false;
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      try {
        const scoped = Boolean(beatId || salesmanUserId || (beatIds && beatIds.length));
        const data = await api.get(`${RECEIVABLES_API}/lookups/customers`, {
          q: query,
          beatId,
          beatIds: beatIds && beatIds.length ? beatIds.join(",") : undefined,
          salesmanUserId: salesmanUserId || undefined,
          limit: scoped && !query ? 500 : 25,
        });
        setOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        setOptions([]);
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, beatId, (beatIds || []).join(","), salesmanUserId, open]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) {
            clearedByTyping.current = true;
            onChange(null);
          }
        }}
        style={{ width: "100%" }}
      />
      {open && options.length > 0 ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-md)",
          }}
        >
          {options.map((customer) => (
            <div
              key={customer.id}
              role="option"
              aria-selected={value?.id === customer.id}
              onMouseDown={() => {
                onChange(customer);
                setQuery(pickerLabel(customer));
                setOpen(false);
              }}
              style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}
            >
              <div>{pickerLabel(customer)}</div>
              {customer.phone || customer.beatName || customer.salesmanName ? (
                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                  {[customer.beatName, customer.salesmanName, customer.phone].filter(Boolean).join(" · ")}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------- data hooks

/**
 * Loads dropdown sources once. Pass the lists you need, e.g. useReceivableLookups(["salesmen", "beats"]).
 * Beats are loaded unfiltered; filter them client-side by salesmanUserId / visitDay.
 */
export const useReceivableLookups = (kinds) => {
  const [lookups, setLookups] = useState({ salesmen: [], beats: [], deliveryPersons: [], fieldUsers: [], banks: [] });
  const key = kinds.join(",");

  useEffect(() => {
    let cancelled = false;
    const paths = {
      salesmen: "/lookups/salesmen",
      beats: "/lookups/beats",
      deliveryPersons: "/lookups/delivery-persons",
      fieldUsers: "/lookups/field-users",
      banks: "/lookups/banks",
    };
    Promise.all(
      key.split(",").filter(Boolean).map(async (kind) => {
        try {
          const data = await api.get(`${RECEIVABLES_API}${paths[kind]}`);
          return [kind, Array.isArray(data) ? data : []];
        } catch (error) {
          toast.error(error.message || `Failed to load ${kind}`);
          return [kind, []];
        }
      })
    ).then((entries) => {
      if (!cancelled) setLookups((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return lookups;
};

export const useReceivableSettings = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api
      .get(`${RECEIVABLES_API}/settings`)
      .then(setSettings)
      .catch(() => setSettings(null));
  }, []);
  return settings;
};

/** Click-to-sort helper for plain tables. */
export const useSort = (rows, initialKey = null) => {
  const [sort, setSort] = useState({ key: initialKey, dir: "asc" });
  const sorted = !sort.key
    ? rows
    : [...rows].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const cmp =
          typeof av === "number" || (!Number.isNaN(Number(av)) && !Number.isNaN(Number(bv)) && av !== "" && bv !== "")
            ? Number(av) - Number(bv)
            : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
        return sort.dir === "asc" ? cmp : -cmp;
      });
  const toggle = (key) => setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
  const indicator = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");
  return { sorted, toggle, indicator };
};
